// A small QR code encoder (brief 035, P-9): byte mode, error correction level M,
// versions 1–3 (up to 42 bytes, enough for a medxmeet.com link). Drawn on the
// device; no library or outside service. Returns a square boolean matrix, true = dark.

// Per version, level M: [data codewords, error-correction codewords, alignment centre or 0].
const SPEC = { 1: [16, 10, 0], 2: [28, 16, 18], 3: [44, 26, 22] };

// GF(256) with the QR polynomial x^8 + x^4 + x^3 + x^2 + 1.
const EXP = new Array(512);
const LOG = new Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d; }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();
const mul = (a, b) => (a && b ? EXP[LOG[a] + LOG[b]] : 0);

// Reed–Solomon error-correction codewords for `data`.
export const rsEncode = (data, n) => {
  let gen = [1];
  for (let i = 0; i < n; i++) {
    const next = new Array(gen.length + 1).fill(0);
    gen.forEach((g, j) => { next[j] ^= g; next[j + 1] ^= mul(g, EXP[i]); });
    gen = next;
  }
  const rem = new Array(n).fill(0);
  data.forEach(byte => {
    const f = byte ^ rem.shift();
    rem.push(0);
    for (let j = 0; j < n; j++) rem[j] ^= mul(gen[j + 1], f);
  });
  return rem;
};

// 15-bit format information for level M (bits 00) and a mask.
const formatBits = mask => {
  const d = (0b00 << 3) | mask;
  let r = d << 10;
  for (let i = 14; i >= 10; i--) if ((r >> i) & 1) r ^= 0x537 << (i - 10);
  return ((d << 10) | r) ^ 0x5412;
};

const MASKS = [
  (r, c) => (r + c) % 2 === 0,
  r => r % 2 === 0,
  (r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

export function qrMatrix(text) {
  // UTF-8 bytes (without TextEncoder, which the test environment lacks).
  const bytes = [...unescape(encodeURIComponent(text))].map(ch => ch.charCodeAt(0));
  const version = [1, 2, 3].find(v => 4 + 8 + bytes.length * 8 <= SPEC[v][0] * 8);
  if (!version) throw new Error('Text too long for this QR encoder.');
  const [dataLen, eccLen, align] = SPEC[version];
  const size = 17 + 4 * version;

  // Data bits: mode 0100, 8-bit length, the bytes, terminator, then pad codewords.
  const bits = [];
  const put = (v, n) => { for (let i = n - 1; i >= 0; i--) bits.push((v >> i) & 1); };
  put(0b0100, 4); put(bytes.length, 8); bytes.forEach(b => put(b, 8));
  put(0, Math.min(4, dataLen * 8 - bits.length));
  while (bits.length % 8) bits.push(0);
  const data = [];
  for (let i = 0; i < bits.length; i += 8) data.push(parseInt(bits.slice(i, i + 8).join(''), 2));
  for (let pad = 0xec; data.length < dataLen; pad ^= 0xec ^ 0x11) data.push(pad);
  const codewords = [...data, ...rsEncode(data, eccLen)];

  // Function patterns.
  const m = Array.from({ length: size }, () => new Array(size).fill(false));
  const fixed = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (r, c, dark) => { m[r][c] = dark; fixed[r][c] = true; };
  const finder = (r0, c0) => {
    for (let r = -1; r <= 7; r++) for (let c = -1; c <= 7; c++) {
      const rr = r0 + r, cc = c0 + c;
      if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
      const ring = Math.max(Math.abs(r - 3), Math.abs(c - 3));
      set(rr, cc, ring !== 2 && ring !== 4);
    }
  };
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);
  for (let i = 8; i < size - 8; i++) { set(6, i, i % 2 === 0); set(i, 6, i % 2 === 0); }
  if (align) for (let r = -2; r <= 2; r++) for (let c = -2; c <= 2; c++) set(align + r, align + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
  set(size - 8, 8, true); // the dark module
  // Reserve the format areas (filled after masking).
  for (let i = 0; i < 9; i++) { if (!fixed[8][i]) set(8, i, false); if (!fixed[i][8]) set(i, 8, false); }
  for (let i = 0; i < 8; i++) { set(8, size - 1 - i, false); set(size - 1 - i, 8, false); }

  // Data placement: two-column zigzag from the bottom right, skipping column 6.
  const all = [];
  codewords.forEach(cw => { for (let i = 7; i >= 0; i--) all.push((cw >> i) & 1); });
  let k = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const c = right - j;
        const upward = ((right + 1) & 2) === 0;
        const r = upward ? size - 1 - vert : vert;
        if (!fixed[r][c]) m[r][c] = k < all.length ? !!all[k++] : false;
      }
    }
  }

  // Try every mask; keep the one with the lowest penalty.
  const withMask = mask => {
    const g = m.map((row, r) => row.map((v, c) => (fixed[r][c] ? v : v !== MASKS[mask](r, c))));
    const f = formatBits(mask);
    const bit = i => ((f >> i) & 1) === 1;
    // g[row][column]: the first copy around the top-left finder, the second split
    // between the top-right and bottom-left ones.
    for (let i = 0; i <= 5; i++) g[i][8] = bit(i);
    g[7][8] = bit(6); g[8][8] = bit(7); g[8][7] = bit(8);
    for (let i = 9; i < 15; i++) g[8][14 - i] = bit(i);
    for (let i = 0; i < 8; i++) g[8][size - 1 - i] = bit(i);
    for (let i = 8; i < 15; i++) g[size - 15 + i][8] = bit(i);
    g[size - 8][8] = true;
    return g;
  };
  const penalty = g => {
    let p = 0;
    const lines = [...g, ...g[0].map((_, c) => g.map(row => row[c]))];
    lines.forEach(line => {
      let run = 1;
      for (let i = 1; i <= line.length; i++) {
        if (i < line.length && line[i] === line[i - 1]) run++;
        else { if (run >= 5) p += run - 2; run = 1; }
      }
      const s = line.map(v => (v ? 1 : 0)).join('');
      p += 40 * ((s.match(/(?=10111010000|00001011101)/g) || []).length);
    });
    for (let r = 0; r < size - 1; r++) for (let c = 0; c < size - 1; c++) {
      const v = g[r][c];
      if (g[r][c + 1] === v && g[r + 1][c] === v && g[r + 1][c + 1] === v) p += 3;
    }
    const dark = g.flat().filter(Boolean).length;
    p += Math.floor(Math.abs(dark * 20 - size * size * 10) / (size * size)) * 10;
    return p;
  };
  let best = null;
  for (let mask = 0; mask < 8; mask++) {
    const g = withMask(mask);
    const p = penalty(g);
    if (!best || p < best.p) best = { g, p };
  }
  return best.g;
}

// An SVG path ("M x y h1 v1 h-1 z" per dark module) for a 4-module quiet zone.
export const qrPath = matrix => {
  let d = '';
  matrix.forEach((row, r) => row.forEach((dark, c) => { if (dark) d += `M${c + 4} ${r + 4}h1v1h-1z`; }));
  return { d, size: matrix.length + 8 };
};
